import { useMemo } from "react"

import { connectorReachesAgent } from "@productlane/lib/src/browser"

import type { RouterOutputs } from "@/lib/api"
import { api } from "@/lib/api"

export type ConnectorSummary =
	RouterOutputs["mcpActivity"]["connectorSummary"][number]

export function useConnectorSummaries(): Map<string, ConnectorSummary> {
	const query = api.mcpActivity.connectorSummary.useQuery(
		{ days: 7 },
		{ staleTime: 60_000 },
	)
	return useMemo(
		() => new Map((query.data ?? []).map((row) => [row.sectionId, row])),
		[query.data],
	)
}

export interface ConnectorStatus {
	line: string
	tone: "ok" | "bad" | "neutral"
	signedOut: boolean
}

// What a connector row says under its name: trouble first, since that is the
// one thing an admin has to act on, then who can use it.
export function connectorStatus(
	section: {
		type: "HTTP" | "MCP"
		availableToAgent: boolean
		allowedMcpTools: readonly string[]
	},
	summary: Pick<ConnectorSummary, "health" | "failed" | "calls"> | null,
): ConnectorStatus {
	if (summary?.health === "signed_out") {
		return {
			line: "Signed out. Its actions are paused.",
			tone: "bad",
			signedOut: true,
		}
	}
	if (summary?.health === "failing") {
		return {
			line: `The last call failed. ${summary.failed} of ${summary.calls} failed this week.`,
			tone: "bad",
			signedOut: false,
		}
	}

	const agentTools = section.allowedMcpTools.length
	const access =
		section.type === "MCP"
			? agentTools > 0
				? `${agentTools} ${agentTools === 1 ? "action" : "actions"} for the agent`
				: "Team only"
			: connectorReachesAgent(section)
				? "Agent and team"
				: "Team only"
	return {
		line: access,
		tone: summary?.health === "ok" ? "ok" : "neutral",
		signedOut: false,
	}
}
